import { useEffect } from 'react';
import { motion } from 'framer-motion';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/FadeIn';

export const PlanosFazendaLeiteira = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const plans = [
    {
      name: 'Starter',
      price: 'R$ 49',
      desc: 'Para pequenas propriedades que querem sair do caderno e começar a medir melhor.',
      highlight: false,
      features: [
        'Cadastro de animais',
        'Controle de produção de leite',
        'Lançamento de receitas e despesas',
        'Relatórios básicos',
        '1 usuário',
      ],
    },
    {
      name: 'Profissional',
      price: 'R$ 99',
      desc: 'Para produtores que querem acompanhar custos, produtividade e lucratividade com mais clareza.',
      highlight: true,
      features: [
        'Tudo do Starter',
        'Controle de reprodução e saúde',
        'Estoque de insumos',
        'Custo por litro',
        'Indicadores financeiros',
        'Alertas importantes',
        'Até 3 usuários',
      ],
    },
    {
      name: 'Enterprise',
      price: 'Sob consulta',
      desc: 'Para fazendas maiores, consultorias, associações ou operações com necessidades específicas.',
      highlight: false,
      features: [
        'Tudo do Profissional',
        'Multiunidades',
        'Usuários ilimitados',
        'Relatórios personalizados',
        'Treinamento da equipe',
        'Suporte prioritário',
      ],
    },
  ];

  const compare = [
    ['Cadastro de animais', true, true, true],
    ['Produção diária de leite', true, true, true],
    ['Receitas e despesas', true, true, true],
    ['Controle de reprodução', false, true, true],
    ['Controle de saúde', false, true, true],
    ['Estoque de insumos', false, true, true],
    ['Custo por litro', false, true, true],
    ['Relatórios personalizados', false, false, true],
    ['Multiunidades', false, false, true],
    ['Suporte prioritário', false, false, true],
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <section
        style={{
          padding: '6rem 0',
          background:
            'radial-gradient(circle at top left, rgba(34,197,94,.24), transparent 32%), linear-gradient(135deg, #052e16 0%, #064e3b 45%, #020617 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.16, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            top: '-22%',
            right: '-12%',
            width: '620px',
            height: '620px',
            background:
              'radial-gradient(circle, rgba(34,197,94,.16) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <FadeIn>
            <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '.5rem',
                  padding: '.5rem 1rem',
                  background: 'rgba(34,197,94,.12)',
                  border: '1px solid rgba(74,222,128,.32)',
                  borderRadius: '999px',
                  color: '#86efac',
                  fontSize: '.9rem',
                  fontWeight: 700,
                  marginBottom: '1.5rem',
                }}
              >
                <i className="fas fa-cow" />
                Planos Fazenda Leiteira
              </div>

              <h1
                style={{
                  fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
                  fontWeight: 900,
                  color: '#fff',
                  lineHeight: 1.05,
                  marginBottom: '1.5rem',
                  letterSpacing: '-1.5px',
                }}
              >
                Escolha o plano ideal para controlar sua{' '}
                <span
                  style={{
                    background:
                      'linear-gradient(135deg, #86efac, #22c55e, #14b8a6)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  propriedade leiteira
                </span>
              </h1>

              <p
                style={{
                  fontSize: '1.18rem',
                  color: '#d1fae5',
                  lineHeight: 1.7,
                  maxWidth: '760px',
                  margin: '0 auto',
                }}
              >
                Comece simples e evolua conforme sua fazenda cresce. Todos os
                planos ajudam você a transformar registros diários em decisões.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section style={{ padding: '5.5rem 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <FadeIn>
            <SectionHeader
              title="Planos simples e objetivos"
              subtitle="Escolha o nível de controle que faz sentido para sua rotina hoje."
            />
          </FadeIn>

          <StaggerContainer
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginTop: '3rem',
              alignItems: 'stretch',
            }}
          >
            {plans.map((plan) => (
              <StaggerItem key={plan.name}>
                <motion.div
                  whileHover={{ y: -8 }}
                  style={{
                    height: '100%',
                    padding: '2rem',
                    borderRadius: '28px',
                    background: plan.highlight
                      ? 'linear-gradient(135deg, rgba(34,197,94,.16), rgba(20,184,166,.08))'
                      : 'var(--bg-tertiary)',
                    border: plan.highlight
                      ? '1px solid rgba(34,197,94,.38)'
                      : '1px solid rgba(148,163,184,.18)',
                    boxShadow: plan.highlight
                      ? '0 24px 70px rgba(34,197,94,.18)'
                      : '0 18px 45px rgba(15,23,42,.08)',
                    position: 'relative',
                  }}
                >
                  {plan.highlight && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        padding: '.35rem .7rem',
                        borderRadius: '999px',
                        background: '#22c55e',
                        color: '#fff',
                        fontSize: '.75rem',
                        fontWeight: 800,
                      }}
                    >
                      Mais indicado
                    </div>
                  )}

                  <h3
                    style={{
                      color: 'var(--dark)',
                      fontSize: '1.6rem',
                      marginBottom: '.75rem',
                    }}
                  >
                    {plan.name}
                  </h3>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: '.35rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <strong
                      style={{
                        color: 'var(--dark)',
                        fontSize: plan.price === 'Sob consulta' ? '2rem' : '2.8rem',
                        lineHeight: 1,
                      }}
                    >
                      {plan.price}
                    </strong>

                    {plan.price !== 'Sob consulta' && (
                      <span style={{ color: 'var(--gray)', marginBottom: '.3rem' }}>
                        /mês
                      </span>
                    )}
                  </div>

                  <p
                    style={{
                      color: 'var(--gray)',
                      lineHeight: 1.7,
                      marginBottom: '1.5rem',
                    }}
                  >
                    {plan.desc}
                  </p>

                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: '0 0 2rem',
                      display: 'grid',
                      gap: '.85rem',
                    }}
                  >
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '.75rem',
                          color: 'var(--dark)',
                          lineHeight: 1.45,
                        }}
                      >
                        <i
                          className="fas fa-check"
                          style={{
                            color: '#22c55e',
                            marginTop: '.2rem',
                          }}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    to="/contato"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {plan.price === 'Sob consulta'
                      ? 'Solicitar Proposta'
                      : 'Começar Agora'}
                  </Button>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section style={{ padding: '5.5rem 0', background: 'var(--bg-tertiary)' }}>
        <div className="container">
          <FadeIn>
            <SectionHeader
              title="Compare os recursos"
              subtitle="Veja o que está incluído em cada plano."
            />
          </FadeIn>

          <div
            style={{
              marginTop: '3rem',
              overflowX: 'auto',
              borderRadius: '24px',
              border: '1px solid rgba(148,163,184,.18)',
              background: 'var(--bg-primary)',
              boxShadow: '0 20px 55px rgba(15,23,42,.08)',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '760px',
              }}
            >
              <thead>
                <tr>
                  {['Recurso', 'Starter', 'Profissional', 'Enterprise'].map((head) => (
                    <th
                      key={head}
                      style={{
                        textAlign: head === 'Recurso' ? 'left' : 'center',
                        padding: '1.2rem',
                        color: 'var(--dark)',
                        borderBottom: '1px solid rgba(148,163,184,.18)',
                        background:
                          head === 'Profissional'
                            ? 'rgba(34,197,94,.10)'
                            : 'transparent',
                      }}
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {compare.map(([feature, starter, pro, enterprise]) => (
                  <tr key={feature}>
                    <td
                      style={{
                        padding: '1rem 1.2rem',
                        color: 'var(--dark)',
                        borderBottom: '1px solid rgba(148,163,184,.12)',
                        fontWeight: 600,
                      }}
                    >
                      {feature}
                    </td>

                    {[starter, pro, enterprise].map((enabled, index) => (
                      <td
                        key={index}
                        style={{
                          padding: '1rem 1.2rem',
                          textAlign: 'center',
                          borderBottom: '1px solid rgba(148,163,184,.12)',
                          background:
                            index === 1 ? 'rgba(34,197,94,.06)' : 'transparent',
                        }}
                      >
                        <i
                          className={
                            enabled ? 'fas fa-check-circle' : 'fas fa-minus-circle'
                          }
                          style={{
                            color: enabled ? '#22c55e' : 'var(--gray)',
                            fontSize: '1.1rem',
                            opacity: enabled ? 1 : 0.45,
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={{ padding: '5rem 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <FadeIn>
            <div
              style={{
                borderRadius: '30px',
                padding: '3rem',
                background:
                  'linear-gradient(135deg, rgba(34,197,94,.14), rgba(20,184,166,.08))',
                border: '1px solid rgba(34,197,94,.24)',
                textAlign: 'center',
              }}
            >
              <h2
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  marginBottom: '1rem',
                  color: 'var(--dark)',
                }}
              >
                Não sabe qual plano escolher?
              </h2>

              <p
                style={{
                  color: 'var(--gray)',
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  maxWidth: '760px',
                  margin: '0 auto 2rem',
                }}
              >
                Fale com a gente e vamos indicar o plano mais adequado para o
                tamanho e a rotina da sua propriedade.
              </p>

              <motion.div
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                style={{ display: 'inline-block' }}
              >
                <Button to="/contato">
                  <i className="fab fa-whatsapp" /> Falar com Especialista
                </Button>
              </motion.div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default PlanosFazendaLeiteira;